import type { JSX } from "react";
import type { TFunction } from "i18next";
import { Check, Maximize2 } from "lucide-react";
import { mediaOrientation } from "@ckgo2/shared";
import type { AdminMediaResponse } from "@ckgo2/shared/types";
import { mediaOrientationLabel } from "../MediaFilters";
import type { PickerTileState } from "./pickerModel";

/**
 * Dlaždice okna výběru fotek (vize podnety-tymu-2026-10, F7.11): náhled, ★, orientace,
 * „použito N×“ (jen u použité fotky), stav výběru a tlačítko velkého náhledu. Čistá
 * komponenta bez hooků, `t` v props (precedent `MediaTile`).
 *
 * `MediaTile` stránky Média se nesdílí: klikem otevírá prohlížeč, počet použití ukazuje
 * i nulový, nese zaškrtávátko s rozsahem Shift a koš. Orientaci i její popisek čte z jediného
 * pravidla (`mediaOrientation`, `mediaOrientationLabel`), práh ani popisky nekopíruje.
 *
 * Kořen je `<div>`: tlačítko přes dlaždici a tlačítko velkého náhledu jsou sourozenci
 * (vnořené tlačítko je neplatné HTML a klik na náhled by vybublal do výběru). Dlaždice
 * „V galerii“ nenese `disabled`, ale `aria-disabled` a klik ignoruje: zakázané tlačítko
 * klik nedoručí a ověření, že dlaždice klik nevybere, by nic neměřilo.
 *
 * V okně otevřeném z hero místa (`purpose="hero"`, vize F8.6) nese fotka na výšku v popisku
 * štítek „na výšku, ořízne se“ (`data-media-picker-portrait`); orientace je táž
 * `mediaOrientation`. Bez `purpose` štítek ve stromu není vůbec.
 */

export interface PickerTileProps {
  item: AdminMediaResponse;
  state: PickerTileState;
  t: TFunction;
  onActivate: (id: string) => void;
  onOpenViewer: (id: string) => void;
  /** Okno otevřené z hero místa: fotka na výšku dostane štítek „ořízne se“. */
  purpose?: "hero";
}

const FRAME_CLASS: Record<PickerTileState, string> = {
  selectable: "border-transparent hover:border-gray-200 dark:hover:border-gray-700",
  selected: "border-primary-500 ring-primary-500/30 ring-2",
  "in-gallery": "border-transparent",
  current: "border-primary-300",
};

const BADGE_CLASS =
  "absolute left-1.5 top-1.5 rounded-full px-2 py-0.5 text-[11px] font-semibold shadow";

function stateBadge(state: PickerTileState, t: TFunction): JSX.Element | null {
  if (state === "selected") {
    return (
      <span
        data-media-picker-check=""
        aria-hidden="true"
        className="bg-primary-600 absolute left-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full text-white shadow"
      >
        <Check className="h-3.5 w-3.5" />
      </span>
    );
  }
  if (state === "in-gallery") {
    return (
      <span className={`${BADGE_CLASS} bg-gray-900/75 text-white`}>
        {t("mediaPicker.tile.inGallery")}
      </span>
    );
  }
  if (state === "current") {
    return (
      <span className={`${BADGE_CLASS} bg-primary-600 text-white`}>
        {t("mediaPicker.tile.current")}
      </span>
    );
  }
  return null;
}

export default function PickerTile({
  item,
  state,
  t,
  onActivate,
  onOpenViewer,
  purpose,
}: PickerTileProps): JSX.Element {
  const name = item.alt || item.originalName || item.id;
  const orientation = mediaOrientation(item.width, item.height);
  const portraitWarn = purpose === "hero" && orientation === "portrait";
  const inGallery = state === "in-gallery";
  const openViewerLabel = t("mediaPicker.tile.openViewer");
  return (
    <div
      data-media-picker-tile=""
      data-media-id={item.id}
      data-starred={item.isStarred ? "true" : "false"}
      data-orientation={orientation}
      data-usage-count={item.usageCount}
      data-state={state}
      className={`group relative overflow-hidden rounded-lg border-2 transition-all ${FRAME_CLASS[state]}`}
    >
      <button
        type="button"
        data-media-picker-activate=""
        aria-label={name}
        title={name}
        aria-disabled={inGallery ? "true" : undefined}
        aria-pressed={state === "selected" ? true : undefined}
        aria-current={state === "current" ? "true" : undefined}
        onClick={inGallery ? () => {} : () => onActivate(item.id)}
        className={`block w-full text-left ${inGallery ? "cursor-not-allowed" : ""}`}
      >
        <div className="relative aspect-square overflow-hidden bg-gray-100 dark:bg-gray-800">
          <img
            src={item.thumbUrl}
            alt={item.alt}
            loading="lazy"
            className={`h-full w-full object-cover ${inGallery ? "opacity-60" : ""}`}
          />
        </div>
        <div className="p-1.5">
          <p className="truncate text-xs font-medium text-gray-900 dark:text-white">{name}</p>
          <p className="truncate text-[11px] text-gray-500">
            {mediaOrientationLabel(orientation)}
            {item.usageCount > 0 && (
              <>
                {" · "}
                <span data-media-picker-used="">
                  {t("mediaPicker.tile.used", { count: item.usageCount })}
                </span>
              </>
            )}
          </p>
          {portraitWarn && (
            <p
              data-media-picker-portrait=""
              className="truncate text-[11px] font-semibold text-amber-700 dark:text-amber-400"
            >
              {t("mediaPicker.portrait")}
            </p>
          )}
        </div>
      </button>

      {/* Vrstva nad čtvercem náhledu: značky bez ukazatele (klik padne na tlačítko dlaždice)
          a tlačítko velkého náhledu vpravo dole, sourozenec tlačítka dlaždice. */}
      <div className="pointer-events-none absolute inset-x-0 top-0 aspect-square">
        {stateBadge(state, t)}

        {item.isStarred && (
          <span
            aria-label={t("media.page.starred")}
            title={t("media.page.starred")}
            className="absolute right-1.5 top-1.5 text-lg leading-none text-amber-400 drop-shadow"
          >
            ★
          </span>
        )}

        <button
          type="button"
          data-media-picker-open-viewer=""
          aria-label={openViewerLabel}
          title={openViewerLabel}
          onClick={() => onOpenViewer(item.id)}
          className="pointer-events-auto absolute bottom-1.5 right-1.5 rounded-md bg-white/85 p-1 text-gray-700 shadow transition-opacity hover:bg-white focus:opacity-100 group-hover:opacity-100 sm:opacity-70 dark:bg-gray-900/80 dark:text-gray-200"
        >
          <Maximize2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
